/*
 * HB Electronic Systems — landing interactions (vanilla port of the Lovable React page).
 * Depends on globals: window.gsap, window.ScrollTrigger, window.Lenis (loaded via CDN before this file).
 * Mirrors src/lib/scroll.ts + the inline hooks in src/routes/index.tsx 1:1.
 */
(function () {
  "use strict";

  var prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isTouch = window.matchMedia("(hover: none)").matches;

  /* ---------------------------------------------------------------------- */
  /* Smooth scroll (Lenis) + GSAP ScrollTrigger wiring  (useLenis)          */
  /* ---------------------------------------------------------------------- */
  function initScroll() {
    if (!window.gsap || !window.ScrollTrigger) return;
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    if (window.Lenis && !prefersReduced) {
      var lenis = new window.Lenis({
        duration: 1.12,
        smoothWheel: true,
        wheelMultiplier: 0.9,
      });
      lenis.on("scroll", ScrollTrigger.update);
      gsap.ticker.add(function (time) {
        lenis.raf(time * 1000);
      });
      gsap.ticker.lagSmoothing(0);
    }

    initFrameScrub(gsap, ScrollTrigger);
    initReveal(gsap, ScrollTrigger);
  }

  /* Background scrubbed by scroll position — sequência de frames WebP num canvas.
     Substitui o bg.mp4 (mais leve + nítido). Roda igual em desktop e mobile. */
  function buildFrameUrls(canvas) {
    if (window.HB_BG_FRAMES && window.HB_BG_FRAMES.length) return window.HB_BG_FRAMES;
    var count = parseInt(canvas.dataset.bgCount, 10);
    var sample = canvas.dataset.bgSample || "";
    var base = sample ? sample.split("bg_001.webp")[0] : "";
    if (!count || !base) return [];
    var urls = new Array(count);
    for (var i = 0; i < count; i++) {
      urls[i] = base + "bg_" + String(i + 1).padStart(3, "0") + ".webp";
    }
    return urls;
  }

  /* Lazy frame loader — não satura a rede no load (protege o LCP).
     1) Só um "head" pequeno carrega no load (hero + primeiro scrub).
     2) O resto é backfill em requestIdleCallback, DEPOIS da janela de LCP.
     3) O scroll sempre tem prioridade: ensure(i) carrega i + lookahead na hora. */
  function startFrameLoader(frames, imgs, drawIdx, getTarget) {
    var HEAD = Math.min(16, frames.length); // suficiente pro topo da página
    var HEAD_CONCURRENT = 6;
    var BACKFILL_CONCURRENT = 4;
    var LOOKAHEAD = 4;
    var queue = [];
    var qi = 0;
    var inflight = 0;
    var backfillStarted = false;

    function loadIndex(i) {
      if (i < 0 || i >= frames.length) return;
      var existing = imgs[i];
      if (existing && (existing.complete || existing.__hbLoading)) return;
      inflight++;
      var im = new Image();
      im.__hbLoading = true;
      im.decoding = "async";
      im.onload = function () {
        inflight--;
        imgs[i] = im;
        if (i === getTarget() || getTarget() < 0) drawIdx(i);
        pump();
      };
      im.onerror = function () {
        im.__hbLoading = false;
        inflight--;
        pump();
      };
      im.src = frames[i];
      imgs[i] = im;
    }

    function pump() {
      var cap = backfillStarted ? BACKFILL_CONCURRENT : HEAD_CONCURRENT;
      while (inflight < cap && qi < queue.length) loadIndex(queue[qi++]);
    }

    /* 1) Só o head agora — deixa banda livre pro LCP. */
    for (var p = 0; p < HEAD; p++) queue.push(p);
    pump();

    /* 2) Backfill do resto só depois da página assentar (pós-LCP). */
    function backfill() {
      if (backfillStarted) return;
      backfillStarted = true;
      for (var r = HEAD; r < frames.length; r++) queue.push(r);
      pump();
    }
    function kickBackfill() {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(backfill, { timeout: 3000 });
      } else {
        setTimeout(backfill, 1500);
      }
    }
    setTimeout(kickBackfill, 1200); // atrasa além da janela de medição do LCP

    /* 3) Scroll = prioridade: carrega o alvo + lookahead imediatamente. */
    return function ensure(i) {
      for (var d = 0; d <= LOOKAHEAD; d++) {
        loadIndex(i + d);
        loadIndex(i - d);
      }
    };
  }

  function initFrameScrub(gsap, ScrollTrigger) {
    var canvas = document.querySelector("[data-bg-canvas]");
    if (!canvas) return;
    var frames = buildFrameUrls(canvas);
    if (!frames.length) return;
    if (prefersReduced) return;

    var ctx = canvas.getContext("2d", { alpha: false });
    var imgs = new Array(frames.length);
    var cur = -1, target = 0;
    var dpr = 1;

    function coverDraw(img) {
      if (!img || !img.complete || !img.naturalWidth) return;
      var cw = canvas.width, ch = canvas.height;
      var ir = img.naturalWidth / img.naturalHeight, cr = cw / ch;
      var w, h, x, y;
      if (cr > ir) { w = cw; h = cw / ir; x = 0; y = (ch - h) / 2; }
      else { h = ch; w = ch * ir; x = (cw - w) / 2; y = 0; }
      ctx.drawImage(img, x, y, w, h);
    }
    function drawIdx(i) {
      if (i < 0 || i >= frames.length) return;
      cur = i;
      coverDraw(imgs[i]);
    }
    function resize() {
      canvas.width = Math.round(window.innerWidth * dpr);
      canvas.height = Math.round(window.innerHeight * dpr);
      if (cur >= 0) drawIdx(cur);
    }

    var ensureFrame = startFrameLoader(frames, imgs, drawIdx, function () { return target; });
    resize();
    window.addEventListener("resize", resize, { passive: true });

    function schedule(i) {
      target = i;
      ensureFrame(i);
      /* Opt 2+6: draw directly — we're already inside GSAP ticker (rAF).
         Eliminates the extra rAF that added 1-frame latency. */
      if (target !== cur) drawIdx(target);
    }

    ScrollTrigger.create({
      trigger: document.body,
      start: "top top",
      end: "bottom bottom",
      scrub: 0.15,
      onUpdate: function (self) {
        var idx = Math.min(frames.length - 1, Math.floor(self.progress * frames.length));
        schedule(idx);
      },
    });

    schedule(0);
  }

  /* Reveal on scroll for [data-reveal]  (useScrollReveal)                  */
  function initReveal(gsap, ScrollTrigger) {
    var els = document.querySelectorAll("[data-reveal]");
    els.forEach(function (el) {
      gsap.fromTo(
        el,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 85%" },
        }
      );
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Custom cursor (desktop only)  (CursorRing)                             */
  /* ---------------------------------------------------------------------- */
  function initCursor() {
    if (window.matchMedia("(hover: none)").matches) return;
    var dot = document.querySelector("[data-cursor-dot]");
    var ring = document.querySelector("[data-cursor-ring]");
    if (!dot || !ring) return;

    var rx = 0, ry = 0, dx = 0, dy = 0;
    var rafId = 0;
    var idle = true; /* Opt 1: cursor idle detection */

    var onMove = function (e) {
      dx = e.clientX; dy = e.clientY;
      dot.style.transform = "translate(" + dx + "px," + dy + "px)";
      if (idle) { idle = false; rafId = requestAnimationFrame(loop); }
    };
    var loop = function () {
      rx += (dx - rx) * 0.2; ry += (dy - ry) * 0.2;
      /* Stop when converged (< 0.5px diff) — frees up frame budget */
      if (Math.abs(dx - rx) < 0.5 && Math.abs(dy - ry) < 0.5) {
        rx = dx; ry = dy;
        ring.style.transform = "translate(" + rx + "px," + ry + "px)";
        idle = true;
        return;
      }
      ring.style.transform = "translate(" + rx + "px," + ry + "px)";
      rafId = requestAnimationFrame(loop);
    };
    var over = function () { document.documentElement.classList.add("cursor-hot"); };
    var out = function () { document.documentElement.classList.remove("cursor-hot"); };

    window.addEventListener("mousemove", onMove, { passive: true }); /* Opt 9 */
    document.querySelectorAll("a,button").forEach(function (el) {
      el.addEventListener("mouseenter", over);
      el.addEventListener("mouseleave", out);
    });
    /* Don't start loop eagerly — first mousemove will kick it off */
  }

  /* ---------------------------------------------------------------------- */
  /* 3D tilt on product cards  (ProductCard onMouseMove/onMouseLeave)       */
  /* ---------------------------------------------------------------------- */
  function initTilt() {
    document.querySelectorAll(".tilt").forEach(function (el) {
      el.addEventListener("mousemove", function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        el.style.setProperty("--ry", px * 7 + "deg");
        el.style.setProperty("--rx", -py * 7 + "deg");
        el.style.setProperty("--ty", "-6px");
      });
      el.addEventListener("mouseleave", function () {
        el.style.setProperty("--rx", "0deg");
        el.style.setProperty("--ry", "0deg");
        el.style.setProperty("--ty", "0px");
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Moto selector  (SeletorMoto)                                           */
  /* ---------------------------------------------------------------------- */
  function initSeletor() {
    var root = document.querySelector("[data-seletor]");
    if (!root) return;
    var buttons = root.querySelectorAll("[data-model-btn]");
    var result = root.querySelector("[data-seletor-result]");
    var countEl = root.querySelector("[data-seletor-count]");
    var modelEl = root.querySelector("[data-seletor-model]");
    var waEl = root.querySelector("[data-seletor-wa]");
    var cards = root.querySelectorAll("[data-seletor-cards] [data-product-card]");

    var activeClasses = ["pill-red", "text-white", "scale-[1.04]"];
    var idleClasses = ["glass-panel", "hover:glass-panel-hover", "text-white/80"];
    var current = null;

    function setButtonState(btn, active) {
      if (active) {
        idleClasses.forEach(function (c) { btn.classList.remove(c); });
        activeClasses.forEach(function (c) { btn.classList.add(c); });
      } else {
        activeClasses.forEach(function (c) { btn.classList.remove(c); });
        idleClasses.forEach(function (c) { btn.classList.add(c); });
      }
    }

    function select(model) {
      current = model;
      buttons.forEach(function (b) { setButtonState(b, b.getAttribute("data-model") === model); });

      if (!model) {
        cards.forEach(function (card, i) {
          card.style.display = "";
          var numEl = card.querySelector("[data-card-num]");
          if (numEl) numEl.textContent = "0" + (i + 1);
        });
        if (countEl) countEl.textContent = "Todos os módulos · ";
        if (modelEl) modelEl.textContent = "escolha sua moto";
        result.hidden = false;
        return;
      }

      var n = 0;
      cards.forEach(function (card) {
        var models = (card.getAttribute("data-models") || "").split(",");
        var match = models.indexOf(model) !== -1;
        card.style.display = match ? "" : "none";
        if (match) {
          n++;
          var numEl = card.querySelector("[data-card-num]");
          if (numEl) numEl.textContent = "0" + n; // renumera 01.. como no original (idx filtrado)
        }
      });

      countEl.textContent = n + " módulo" + (n > 1 ? "s" : "") + " compatíve" + (n > 1 ? "is" : "l") + " com ";
      modelEl.textContent = model;
      if (waEl) {
        var msg = "Olá! Tenho uma " + model + " e quero ajuda pra escolher o módulo HB.";
        waEl.href = "https://wa.me/5511987657788?text=" + encodeURIComponent(msg);
      }
      result.hidden = false;
    }

    buttons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var model = btn.getAttribute("data-model");
        select(current === model ? null : model);
      });
    });

    // Estado inicial: mostra todos os módulos (evita slot vazio gigante)
    select(null);
  }

  /* ---------------------------------------------------------------------- */
  /* Animated dashboard gauge  (DashboardMockup)                            */
  /* ---------------------------------------------------------------------- */
  function initDashboard() {
    var dash = document.querySelector("[data-dash]");
    if (!dash) return;
    var needle = dash.querySelector("[data-dash-needle]");
    var speedEl = dash.querySelector("[data-dash-speed]");
    var gearEl = dash.querySelector("[data-dash-gear]");
    var segs = dash.querySelectorAll("[data-shift-seg]");

    var shiftColors = [
      "#22c55e", "#22c55e", "#22c55e",
      "#eab308", "#eab308", "#eab308",
      "#f97316", "#f97316",
      "#e11d23", "#e11d23", "#e11d23",
    ];
    var angleFor = function (n) { return -135 + (n / 15) * 270; };

    function render(rpm, speed, gear) {
      if (needle) needle.style.transform = "translate(-50%, -100%) rotate(" + angleFor(rpm) + "deg)";
      if (speedEl) speedEl.textContent = String(Math.round(speed));
      if (gearEl) gearEl.textContent = String(gear);
      var lit = rpm < 0.3 ? 0 : Math.min(11, Math.ceil((rpm / 15) * 11));
      segs.forEach(function (seg, i) {
        var on = i < lit;
        seg.style.opacity = on ? "1" : "0.2";
        seg.style.boxShadow = on ? "0 0 6px " + shiftColors[i] : "none";
      });
    }

    if (prefersReduced) { render(8, 72, 4); return; }

    var duration = 4200;
    var keyframes = [
      { p: 0, rpm: 0, speed: 0 },
      { p: 0.55, rpm: 14, speed: 118 },
      { p: 0.68, rpm: 10.5, speed: 86 },
      { p: 0.82, rpm: 12.5, speed: 102 },
      { p: 1, rpm: 0, speed: 0 },
    ];
    var sample = function (t) {
      for (var i = 0; i < keyframes.length - 1; i++) {
        var a = keyframes[i], b = keyframes[i + 1];
        if (t >= a.p && t <= b.p) {
          var k = (t - a.p) / (b.p - a.p);
          return { rpm: a.rpm + (b.rpm - a.rpm) * k, speed: a.speed + (b.speed - a.speed) * k };
        }
      }
      return keyframes[0];
    };
    var gearFromRpm = function (r, spd) {
      if (spd < 2) return "N";
      if (r < 4) return 2;
      if (r < 7) return 3;
      if (r < 10) return 4;
      if (r < 12) return 5;
      return 6;
    };

    /* Opt 4: IntersectionObserver — only animate when visible */
    var running = false, rafId = 0;
    var start = performance.now();
    var tick = function (now) {
      if (!running) return;
      var t = ((now - start) % duration) / duration;
      var s = sample(t);
      render(s.rpm, s.speed, gearFromRpm(s.rpm, s.speed));
      rafId = requestAnimationFrame(tick);
    };
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting && !running) {
          running = true;
          start = performance.now();
          rafId = requestAnimationFrame(tick);
        } else if (!e.isIntersecting && running) {
          running = false;
          cancelAnimationFrame(rafId);
        }
      });
    }, { threshold: 0.1 });
    obs.observe(dash);
  }

  /* ---------------------------------------------------------------------- */
  /* FAQ accordion  (Faq)                                                   */
  /* ---------------------------------------------------------------------- */
  function initFaq() {
    var items = document.querySelectorAll("[data-faq-item]");
    if (!items.length) return;
    items.forEach(function (item) {
      var btn = item.querySelector("[data-faq-btn]");
      var answer = item.querySelector("[data-faq-answer]");
      var plus = item.querySelector("[data-faq-plus]");
      if (!btn) return;
      btn.addEventListener("click", function () {
        var isOpen = !answer.hidden;
        // fecha todos
        items.forEach(function (other) {
          var a = other.querySelector("[data-faq-answer]");
          var p = other.querySelector("[data-faq-plus]");
          if (a) a.hidden = true;
          if (p) p.classList.remove("rotate-45");
        });
        // abre o clicado (se estava fechado)
        if (!isOpen) {
          answer.hidden = false;
          if (plus) plus.classList.add("rotate-45");
        }
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Grain overlay + first-frame paint of bg video                         */
  /* ---------------------------------------------------------------------- */
  function initMisc() {
    document.documentElement.classList.add("grain");
  }

  /* ---------------------------------------------------------------------- */
  function boot() {
    initMisc();
    initScroll();
    if (!isTouch) initCursor();
    initTilt();
    initSeletor();
    initDashboard();
    initFaq();
  }

  if (document.readyState === "complete") {
    boot();
  } else {
    window.addEventListener("load", boot);
  }
})();
